import { apolloClient } from "apollo";
import { GET_ASSESSMENT_REPORT_LINK } from "api/graphql/user";

// Ropes shareable report links expire 90 days after the assessment. Treat them
// as expired after 85 days to leave a safety margin. Mirrors the backend
// getAssessmentReportLink resolver (torc-serverless #2363).
const REPORT_LINK_VALIDITY_DAYS = 85;
const REPORT_LINK_VALIDITY_MS = REPORT_LINK_VALIDITY_DAYS * 24 * 60 * 60 * 1000;

const isRopesAssessment = (assessment) =>
  (assessment?.source || "").toUpperCase() === "ROPES";

/**
 * Behavioral integrity risk rating for a completed assessment. Replaces the
 * deprecated `plagiarism` field (search-tool #2234).
 *
 * The rating is a free-form string on the API rather than an enum, and is only
 * present once the assessment has actually been rated - callers must handle a
 * missing value rather than assuming a clean result.
 *
 * @param {Object} assessment the assessment holding the integrity report
 * @returns {string|null} the risk rating, or null if the assessment was never rated
 */
export const getIntegrityRisk = (assessment) =>
  assessment?.resultDetails?.integrityReport?.risk || null;

/**
 * Explanation accompanying the integrity risk rating, when the report has one.
 *
 * @param {Object} assessment the assessment holding the integrity report
 * @returns {string} the explanation, or an empty string when absent
 */
export const getIntegrityRiskExplanation = (assessment) =>
  assessment?.resultDetails?.integrityReport?.explanation || "";

/**
 * Display label for an integrity risk rating. The rating carries only the
 * severity (e.g. "low"), which inverts its meaning when shown under an
 * "Integrity" heading - "Integrity: Low" reads as poor integrity, the opposite
 * of "Integrity: Low Risk". Render the rating through this so the noun always
 * travels with it.
 *
 * @param {string} [risk] the risk rating
 * @returns {string} the rating qualified with "Risk", or an empty string when absent
 */
export const formatIntegrityRisk = (risk) => {
  if (!risk) {
    return "";
  }

  // "none" is a rating the provider does emit, and reads as "No Risk" rather
  // than "None Risk".
  if (risk.toLowerCase() === "none") {
    return "No Risk";
  }

  // The badges force uppercase, but the tooltips this also feeds do not, so
  // normalise the provider's casing rather than surfacing a raw "low".
  const severity = risk.charAt(0).toUpperCase() + risk.slice(1).toLowerCase();

  // The rating is a free-form string from the assessment provider rather than an
  // enum, so tolerate one that already carries the noun instead of rendering
  // "Low Risk Risk".
  return /\brisk\b/i.test(severity) ? severity : `${severity} Risk`;
};

/**
 * Colour treatment for an integrity risk badge, keyed off the severity of the
 * rating. Shared by every surface that renders the badge so the treatment stays
 * consistent; an unrecognised or absent rating falls back to a neutral slate.
 *
 * @param {string} [risk] the risk rating
 * @returns {string} tailwind classes for background, text and border
 */
export const getIntegrityRiskStyles = (risk) => {
  switch (risk?.toLowerCase()) {
    case "low":
      return "bg-emerald-50 text-emerald-700 border-emerald-100";
    case "medium":
      return "bg-amber-50 text-amber-700 border-amber-100";
    case "high":
      return "bg-rose-50 text-rose-700 border-rose-100";
    default:
      return "bg-slate-50 text-slate-700 border-slate-100";
  }
};

/**
 * Whether the assessment's stored report link has (possibly) expired. Only
 * Ropes report links expire - links from other sources are always considered
 * valid.
 *
 * @param {Object} assessment the assessment holding the report link
 * @returns {boolean} true if the stored link can no longer be trusted
 */
export const isAssessmentReportLinkExpired = (assessment) => {
  if (!isRopesAssessment(assessment)) {
    return false;
  }

  // reportLinkExpiresAt is the exact cutoff, stamped when the link was last
  // refreshed by the backend
  if (assessment.reportLinkExpiresAt) {
    return Date.now() >= new Date(assessment.reportLinkExpiresAt).getTime();
  }

  // Otherwise estimate from the completion date, falling back to the record
  // creation date. If neither is available we cannot vouch for the link, so
  // consider it expired.
  const referenceDate = assessment.assessmentCompleted || assessment.createdAt;

  if (!referenceDate) {
    return true;
  }

  return (
    Date.now() >= new Date(referenceDate).getTime() + REPORT_LINK_VALIDITY_MS
  );
};

/**
 * Fetches a working report link for the assessment through the
 * getAssessmentReportLink query, which refreshes the link with Ropes if the
 * stored one expired. Results are cached by the Apollo cache (default
 * cache-first policy) per interviewId / userId pair.
 *
 * @param {Object} assessment the assessment whose link expired
 * @param {string} userId id of the user the assessment belongs to
 * @returns {Promise<string|null>} a working report link
 */
export const fetchFreshAssessmentReportLink = async (assessment, userId) => {
  const { data } = await apolloClient.query({
    query: GET_ASSESSMENT_REPORT_LINK,
    variables: { interviewId: assessment.interviewId, userId },
  });

  return data?.getAssessmentReportLink || null;
};
